import React, { useCallback, useEffect } from 'react';
import ReactDom from 'react-dom';
import { findOrCreateAnnotationLayer } from '../../lib/pdfjs-dom';
import { scaledPositionToViewport } from '../../lib/coordinates';
import { groupResultFieldsByPage, getLabelColorForInboxResultField } from '../../lib/util';
import { PDFViewer } from 'pdfjs-dist/web/pdf_viewer';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { Label } from '../../types/label';
import { Position, ResultField } from '../../types/result';
import useGlobal from '../../store/Store';

const getHighlightedStyleForWord = (
  selectedWord: null | Label,
  word: ResultField,
  position: { left: number; top: number; width: number; height: number },
  type: string
) => {
  const isSelected = selectedWord && word.id === selectedWord.id;

  return {
    backgroundColor: getLabelColorForInboxResultField(word, type),
    border: isSelected ? '2px solid #ff4d4f' : '0px',
    ...position,
  };
};

const alterPosition = (inputPosition: any) => {
  inputPosition.position.top = inputPosition.position.top - 3;
  inputPosition.position.height = inputPosition.position.height + 5;
  return inputPosition;
};

const calculateConnectionOrder = (location1: Position, location2: Position) => {
  return location1.x1 === location2.x1
    ? location1.y1 > location2.y1
      ? 'connectLineD connectLineA'
      : 'connectLineC'
    : location1.x1 > location2.x1
    ? 'connectLineD connectLineA'
    : 'connectLineC';
};

const getClassNamesForWords = (
  resultLabel: Label,
  correctionLabel: Label | undefined,
  selectedWord: Label | null,
  highlightedWord: ResultField
) => {
  let resultClassName = selectedWord && highlightedWord.id === selectedWord.id ? 'connectLineA' : '';
  let correctionClassName = '';

  if (
    resultLabel &&
    correctionLabel &&
    correctionLabel.location &&
    selectedWord &&
    highlightedWord.id === selectedWord.id
  ) {
    resultClassName = resultLabel.location
      ? calculateConnectionOrder(resultLabel.location, correctionLabel.location)
      : '';

    correctionClassName = resultLabel.location
      ? calculateConnectionOrder(correctionLabel.location, resultLabel.location)
      : 'connectLineC';
  }

  return { resultClassName, correctionClassName };
};

type Props = {
  viewer: PDFViewer;
  pdfDocument: PDFDocumentProxy;
  highlightedWords: ResultField[];
  selectedWord: null | Label;
};

export const PdfResultsLayer = (props: Props) => {
  const { viewer, pdfDocument, highlightedWords, selectedWord } = props;

  const [globalState] = useGlobal();

  const renderBoxes = useCallback(
    ({
      pdfDocument,
      viewer,
      selectedWord,
      highlightedWords,
    }: {
      pdfDocument: PDFDocumentProxy;
      viewer: PDFViewer;
      selectedWord: null | Label;
      highlightedWords: ResultField[];
    }) => {
      if (!viewer) {
        return;
      }

      const highlightedWordsByPage: {
        [page: string]: ResultField[];
      } = groupResultFieldsByPage(highlightedWords);

      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
        const annotationLayer = findOrCreateAnnotationLayer(viewer, pageNumber);
        if (annotationLayer) {
          ReactDom.render(
            <div>
              {(highlightedWordsByPage[String(pageNumber)] || []).map(highlightedWord => {
                const resultLabel = globalState.labels.find(l => l.id === highlightedWord.id)!;
                const correctionLabel = globalState.labels.find(l => l.id === highlightedWord.id + '1');

                let scaledPositionResult = null;
                let scaledPositionCorrection = null;

                let { resultClassName, correctionClassName } = getClassNamesForWords(
                  resultLabel,
                  correctionLabel,
                  selectedWord,
                  highlightedWord
                );

                if (resultLabel && resultLabel.exists) {
                  scaledPositionResult = scaledPositionToViewport(viewer, resultLabel.location!, resultLabel.page!);
                  scaledPositionResult = alterPosition(scaledPositionResult);
                }

                if (correctionLabel && correctionLabel.location) {
                  scaledPositionCorrection = scaledPositionToViewport(
                    viewer,
                    correctionLabel.location!,
                    correctionLabel.page!
                  );
                  scaledPositionCorrection = alterPosition(scaledPositionCorrection);
                }

                return (
                  <div key={highlightedWord.id}>
                    {resultLabel && scaledPositionResult && (
                      <div
                        id={resultLabel.id}
                        className={resultClassName}
                        style={{
                          position: 'absolute',
                          opacity: 0.4,
                          ...getHighlightedStyleForWord(
                            selectedWord,
                            highlightedWord,
                            scaledPositionResult.position,
                            'result'
                          ),
                        }}
                      />
                    )}
                    {correctionLabel && scaledPositionCorrection && (
                      <div
                        id={correctionLabel.id}
                        className={correctionClassName}
                        style={{
                          position: 'absolute',
                          opacity: correctionLabel.id === selectedWord?.id + '1' ? 0.4 : 0,
                          ...getHighlightedStyleForWord(
                            selectedWord,
                            highlightedWord,
                            scaledPositionCorrection.position,
                            'correction'
                          ),
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>,
            annotationLayer
          );
        }
      }
    },
    [globalState]
  );

  useEffect(() => {
    renderBoxes({ pdfDocument, viewer, selectedWord, highlightedWords });
  }, [pdfDocument, viewer, renderBoxes, selectedWord, highlightedWords, globalState]);

  return <div></div>;
};
